/**
 * Extends pinch zoom functionality for highcharts map
 */

import HighCharts from 'highcharts';
import 'highcharts/modules/map';

type HighchartsMap = HighCharts.Chart & {
	mapView: any;
};

// The absolute value of the change in the touches distances
// that makes us determine that the user intends to pinch/spread.
const PINCH_PIXEL_TRESHOLD = 15;

class HighchartsMapPinchZoom {
	chart: HighchartsMap;
	lastDistance: number = 0;
	isZooming: boolean = false;

	constructor(chart: HighchartsMap) {
		this.chart = chart;
	}

	static attach(chart: HighchartsMap) {
		const instance = new HighchartsMapPinchZoom(chart);

		instance.disableBuiltIns();
		instance.applyPinchZoom();
	}

	disableBuiltIns() {
		// Remove built-in pinch zoom functionality
		this.chart.update(
			{
				mapNavigation: {
					...(this.chart.options.mapNavigation || {}),
					enableTouchZoom: false,
				},
			},
			true,
			false
		);
	}

	applyPinchZoom() {
		const element = this.chart.container;

		element.addEventListener('touchstart', this.onTouchStart.bind(this));
		element.addEventListener('touchend', this.onTouchEnd.bind(this));
		element.addEventListener('touchmove', this.onTouchMove.bind(this));
	}

	onTouchStart(event: TouchEvent) {
		const { minZoom, zoom } = this.chart.mapView;

		// Check if map is zoomed
		if (minZoom !== zoom || event.touches.length === 2) {
			event.stopPropagation();
			// event.preventDefault();
		}

		if (event.touches.length === 2) {
			this.isZooming = true;
			this.lastDistance = this.computeTouchesDistance(event.touches);
		} else {
			this.isZooming = false;
		}
	}

	onTouchEnd() {
		this.isZooming = false;
		this.lastDistance = 0;
	}

	onTouchMove(event: TouchEvent) {
		// Prevent all other touchmove handlers
		event.stopPropagation();
		event.preventDefault();

		if (event.touches.length === 2 && this.isZooming) {
			const touchesDistance = this.computeTouchesDistance(event.touches);
			const distanceChange = touchesDistance - this.lastDistance;

			// If the change in the touch distance is only between -15 to 15x
			if (Math.abs(distanceChange) <= PINCH_PIXEL_TRESHOLD) {
				return true;
			}

			// Save current computation for the next touchmove event
			this.lastDistance = touchesDistance;

			// Convert px distance into map zoom ratio
			// In Highcharts, 1 zoom is equiv. to half the view
			const zoomRatio = (distanceChange / window.innerWidth) * 2;

			// Apply zoom
			this.chart.mapView.zoomBy(zoomRatio);
		}
	}

	computeTouchesDistance(touches: TouchList) {
		const [touchA, touchB] = Array.from(touches);

		// Compute the distance between two points, using Pythagorean formula.
		const xDistance = Math.abs(touchA.clientX - touchB.clientX);
		const yDistance = Math.abs(touchA.clientY - touchB.clientY);
		const distance = Math.sqrt(xDistance ** 2 + yDistance ** 2);

		return distance;
	}
}

export default HighchartsMapPinchZoom;
